import type { ClientIdentityFields } from "../types/clientIdentity";

/**
 * The client-derived subset of `lookupPrefix()`'s parameters. Signal flags and the insurance
 * provider are not part of client identity, so they stay with the caller.
 */
export interface ClientIdentityResolutionInput {
  practiceId?: string;
  physicianId?: string;
  surgeryCenterLabId?: string;
}

/**
 * Accessioning and mid-cycle correction must resolve the same prefix from the same client
 * values. The resolver itself has always been shared — `POST /prefixes/lookup` — but the
 * derivation of its arguments was not, and this derivation is where the two paths would
 * otherwise drift: accessioning treats the submitting lab as the surgery-center input when one
 * is present, and any caller that skipped that rule would silently resolve a different prefix
 * for identical client values.
 *
 * Mirrors `backend/src/services/lis/clientIdentity/clientIdentityResolutionInput.ts`. The
 * backend coalesces with `??` because its inputs come from nullable database columns; here the
 * inputs come from form state, where a cleared Select is an empty string, so blank is treated
 * as absent.
 */
export function buildClientIdentityResolutionInput(
  fields: ClientIdentityFields
): ClientIdentityResolutionInput {
  const submittingLabId = blankToUndefined(fields.submittingLabId);
  const surgeryCenterLabId = blankToUndefined(fields.surgeryCenterLabId);
  return {
    practiceId: blankToUndefined(fields.sendingPracticeId),
    physicianId: blankToUndefined(fields.physicianId),
    surgeryCenterLabId: submittingLabId ?? surgeryCenterLabId,
  };
}

function blankToUndefined(value?: string | null): string | undefined {
  return value || undefined;
}
